import { toDate } from '@elara/client-core/lib/date-utils';

import { useRouterState, useTripsState, useUserProfileState } from '#states/hooks.js';
import { Alert, AlertDescription, AlertTitle, Loader } from '#ui/index.js';
import { AppLayout } from '#views/layout/AppLayout.jsx';
import { PaymentFailedAlert } from '#views/profiles/PaymentFailedAlert.js';
import { StoryRail } from '#views/stories/StoryRail.jsx';
import { ActiveTripBanner } from '#views/trip/ActiveTripBanner.js';
import { compareTripsByActivityDesc } from '#views/trip/trip-sorting.js';
import { TripList } from '#views/trip/TripList.js';
import { TripsHeader } from '#views/trip/TripsHeader.js';

/**
 * TripsPage component displays the list of trips when no specific trip is selected.
 * This is the landing page that shows all available trips for the user.
 */
export function TripsPage() {
  const routerState = useRouterState();
  const tripsState = useTripsState();
  const userProfileState = useUserProfileState();

  const trips = tripsState.recentTrips;
  const now = new Date();
  // Normalize current date to start of day for day-level comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const currentTrips = trips.items
    .filter((trip) => {
      // Trip with no dates - always show as current
      if (!trip.startDate && !trip.endDate) return true;

      // Trip has not ended yet (ongoing or upcoming)
      // A trip is current until the end date has passed (i.e., until the day after the end date)
      if (trip.endDate) {
        const endDate = toDate(trip.endDate);
        if (endDate) {
          // Normalize end date to start of day for day-level comparison
          const endDateStart = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
          // Trip is current if end date is today or in the future
          if (endDateStart >= today) return true;
        }
      }

      // Trip has not started yet and has no end date (upcoming, open-ended)
      if (!trip.endDate && trip.startDate) {
        const startDate = toDate(trip.startDate);
        if (startDate) {
          // Normalize start date to start of day for day-level comparison
          const startDateStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          if (startDateStart >= today) return true;
        }
      }

      return false;
    })
    // Ordered by most-recent activity (last message sent, trip edit, or creation) so the freshest
    // conversations surface first.
    .toSorted((a, b) => compareTripsByActivityDesc(a, b));

  const pastTrips = trips.items
    .filter((trip) => {
      if (!trip.endDate) return false;
      const endDate = toDate(trip.endDate);
      if (!endDate) return false;
      // Normalize end date to start of day for day-level comparison
      // A trip is past only if the end date is before today
      const endDateStart = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      return endDateStart < today;
    })
    // Past trips are ordered by trip end date, most recent first.
    .toSorted((a, b) => {
      const aDate = toDate(a.endDate!)?.getTime() ?? 0;
      const bDate = toDate(b.endDate!)?.getTime() ?? 0;
      return bDate - aDate;
    });

  // The trip in progress right now (today within its dates) is featured in a banner above the lists and
  // dropped from "Upcoming" so it isn't shown twice.
  const activeTrip = currentTrips.find((trip) => {
    const start = toDate(trip.startDate);
    const end = toDate(trip.endDate);
    if (!start || !end) return false;
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return startDay <= today && today <= endDay;
  });
  const upcomingTrips = activeTrip ? currentTrips.filter((t) => t.id !== activeTrip.id) : currentTrips;

  return (
    <AppLayout transitionKey={routerState.currentRoute?.path}>
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 flex flex-col overflow-scroll">
          {/* Full-bleed band, so it sits outside the content column's max width and padding. */}
          {trips.isReady && upcomingTrips.length > 0 && <TripsHeader />}

          <div className="flex flex-col max-w-7xl mx-auto w-full px-4 pt-8 md:pt-12 pb-16 flex-1 gap-8 md:gap-16">
            {!trips.isReady ? (
              // Display a loading spinner while fetching trips.
              <div className="flex items-center justify-center py-8 flex-1">
                <Loader loading={true} />
              </div>
            ) : (
              <>
                {userProfileState.hasRequestedDeletion && (
                  <Alert variant="destructive">
                    <AlertTitle>Account deletion pending</AlertTitle>
                    <AlertDescription>
                      You’ve requested to delete your account. Our team will email you to confirm.
                    </AlertDescription>
                  </Alert>
                )}

                <PaymentFailedAlert />

                {activeTrip && <ActiveTripBanner trip={activeTrip} />}

                <div className="w-full max-w-7xl mx-auto flex flex-col gap-4.5">
                  {/* The header band carries the page's big type and its primary action, so the list
                      headings stay at section scale. */}
                  {upcomingTrips.length > 0 && activeTrip && <h2 className="text-xl font-display">Upcoming trips</h2>}
                  <TripList trips={upcomingTrips} hasPastTrips={pastTrips.length > 0} />
                </div>

                <StoryRail />

                {pastTrips.length > 0 && (
                  <div className="w-full max-w-7xl mx-auto flex flex-col gap-4 md:gap-6">
                    <h2 className="text-xl font-display">Past trips</h2>
                    <TripList trips={pastTrips} size="sm" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
